import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useAllLocations, type AdminLocation } from '../hooks/useAllLocations';

const OPEN_TICKET_STATUSES = new Set(['open', 'in_progress', 'waiting_on_customer']);

export function LocationsPage() {
  const navigate = useNavigate();
  const { data: locations, isLoading, isError } = useAllLocations();
  const [query, setQuery] = useState('');

  const visibleLocations = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return locations ?? [];
    return (locations ?? []).filter((location) =>
      [location.name, location.customer?.name, formatAddress(location)]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(normalized)),
    );
  }, [locations, query]);

  if (isLoading) {
    return <LoadingScreen label="Loading sites…" />;
  }

  if (isError) {
    return (
      <EmptyState
        icon={<LocationIcon />}
        title="Unable to load sites"
        subtitle="The location records are still in PazPortal. Refresh to try again."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-text sm:text-2xl">Sites</h1>
          <p className="mt-1 text-sm text-text-muted">
            {(locations ?? []).length} customer location{(locations ?? []).length === 1 ? '' : 's'}
          </p>
        </div>
        <label className="w-full sm:w-72">
          <span className="sr-only">Search sites</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search site or customer"
            className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm text-text outline-none focus:ring-2 focus:ring-primary"
          />
        </label>
      </div>

      {visibleLocations.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState
            icon={<LocationIcon />}
            title={query ? 'No matching sites' : 'No sites'}
            subtitle={query ? 'Try another site or customer name.' : 'Sites appear here after customer onboarding.'}
          />
        </div>
      ) : (
        <>
          <div className="space-y-2 md:hidden">
            {visibleLocations.map((location) => (
              <button
                key={location.id}
                type="button"
                onClick={() => navigate(`/locations/${location.id}`)}
                className="w-full rounded-lg border border-border bg-surface p-4 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text">{location.name}</p>
                    <p className="truncate text-sm text-primary">{location.customer?.name ?? 'Unmapped customer'}</p>
                  </div>
                  <StatusBadge status={location.status ?? 'unknown'} />
                </div>
                <p className="mt-2 text-sm text-text-muted">{formatAddress(location) || 'Address not provided'}</p>
                <p className="mt-2 text-xs text-text-faint">
                  {location.deployments.length} deployments · {openTicketCount(location)} open tickets
                </p>
              </button>
            ))}
          </div>

          <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
            <table className="w-full text-sm">
              <thead className="bg-surface-3 text-left text-text-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Site</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Address</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 text-center font-medium">Deployments</th>
                  <th className="px-4 py-3 text-center font-medium">Open tickets</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider bg-surface">
                {visibleLocations.map((location) => (
                  <tr key={location.id} className="hover:bg-surface-strong">
                    <td className="px-4 py-3 font-medium">
                      <Link to={`/locations/${location.id}`} className="text-primary hover:underline">{location.name}</Link>
                    </td>
                    <td className="px-4 py-3">
                      {location.customer ? (
                        <Link to={`/customers/${location.customer.id}`} className="text-primary hover:underline">
                          {location.customer.name}
                        </Link>
                      ) : (
                        <span className="text-text-muted">Unmapped customer</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{formatAddress(location) || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={location.status ?? 'unknown'} /></td>
                    <td className="px-4 py-3 text-center text-text">{location.deployments.length}</td>
                    <td className="px-4 py-3 text-center text-text">{openTicketCount(location)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function openTicketCount(location: AdminLocation): number {
  return location.tickets.filter((ticket) => ticket.status && OPEN_TICKET_STATUSES.has(ticket.status)).length;
}

function formatAddress(location: AdminLocation): string {
  const address = location.address;
  if (!address) return '';
  return [
    address.street,
    [address.city, address.state].filter(Boolean).join(', '),
    address.zip,
  ].filter(Boolean).join(' ');
}

function LoadingScreen({ label }: { label: string }) {
  return (
    <div className="flex h-64 items-center justify-center gap-3 text-sm text-text-muted">
      <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      {label}
    </div>
  );
}

function LocationIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657 13.414 20.9a2 2 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0Z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}
